import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import _ from 'lodash';
import { DropUpIcon } from '../../../components/Icon';
import EADBed from '../components/EADViewFloorPlan/components/EADBed';
import { calculateRemToPx } from '../../../utils/newBedStatusUtil';
import { E_ADMISSION_GRID_SIMENSIONS } from '../../../comm/constant';

const QUEUE_ESTIMATED_ITEM_HEIGHT_REM = 5.143;
const QUEUE_ITEM_GAP_REM = 0.714;
const QUEUE_OVERSCAN_COUNT = 4;
const QUEUE_MIN_RENDER_COUNT = 12;

// Queuebar可能有重复caseNo，key带上index避免虚拟列表复用错行。
const getQueuePatientKey = (item, index) => `${item?.eaId ?? item?.caseNo ?? 'pending'}-${index}`;

const useStyles = makeStyles(() => ({
  container: {
    height: '100%',
    display: 'flex',
  },
  queueBarToggle: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: '1rem',
    color: '#7d86a9',
    backgroundColor: '#FFFFFF',
    padding: '1rem 0.25rem',
    height: 'fit-content',
    cursor: 'pointer',
    boxShadow: '10px 0 20px 0px rgba(198, 211, 230, 0.4)',
    borderRadius: '0 8px 8px 0',
  },
  queueBarArrow: ({ showQueuebar }) => ({
    // width: '100%',
    // height: '1rem',
    transform: showQueuebar ? 'rotate(-90deg)' : 'rotate(90deg)',
  }),
  queueBarText: {
    fontSize: '1rem',
    whiteSpace: 'nowrap',
    transform: 'rotate(180deg)' /* 文字竖向排列 */,
    writingMode: 'vertical-rl' /* 支持竖排文字（现代浏览器）*/,
    textOrientation: 'mixed',
    fontFamily: 'NotoSans-Medium',
  },
  dropIcon: {
    fontSize: '0.875rem',
    color: '#538fff',
  },
  queueBarContent: () => ({
    padding: '0.571rem',
    height: '100%',
    backgroundColor: '#FFF',
    boxShadow: '10px 0 20px 0px rgba(198, 211, 230, 0.4)',
    minWidth: E_ADMISSION_GRID_SIMENSIONS?.width,
    borderRadius: '0 0 8px',
    display: 'flex',
    flexDirection: 'column',
    gridGap: '0.714rem',
  }),
  queueList: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.714rem',
    overflowY: 'scroll',
    scrollbarWidth: 0,
    '&::-webkit-scrollbar': {
      display: 'none' /* Chrome, Safari, Edge */,
    },
  },
  queueTitle: {
    color: '#7D86A9',
    fontSize: '1rem',
    fontWeight: 500,
  },
  virtualList: {
    // 用一个撑开总高度的容器承载可见病人卡。
    position: 'relative',
    flexShrink: 0,
    width: E_ADMISSION_GRID_SIMENSIONS?.width,
    minWidth: E_ADMISSION_GRID_SIMENSIONS?.width,
  },
  virtualRow: {
    // 病人卡按计算出的top定位，避免一次渲染整条queue。
    position: 'absolute',
    left: 0,
    right: 0,
    width: '100%',
  },
}));

const QueueBar = forwardRef(
  (
    {
      showQueuebar,
      setShowQueuebar,
      queuePatientList,
      selectedPatient,
      handleSelectPatient,
      handleOpenPatientDetail,
      queueTitle = 'Queue',
      queuebarStorageKey,
      showDetails = true,
      patientInfoViewType = '',
    },
    ref
  ) => {
    const classes = useStyles({ showQueuebar });
    const queueListRef = useRef(null);
    const virtualListRef = useRef(null);
    const heightFrameRef = useRef(null);
    const measureFrameRef = useRef(null);
    const scrollFrameRef = useRef(null);
    const pendingScrollTopRef = useRef(0);
    const rowHeightsRef = useRef({});
    const rowObserversRef = useRef({});
    const [scrollTop, setScrollTop] = useState(0);
    const [queueListHeight, setQueueListHeight] = useState(0);
    const [virtualListTop, setVirtualListTop] = useState(0);
    const [rowMetrics, setRowMetrics] = useState({
      estimatedHeight: 72,
      gap: 10,
    });
    const [heightVersion, setHeightVersion] = useState(0);
    const queuePatientCount = queuePatientList?.length ?? 0;

    // 多个病人卡测高结果合并到一帧更新，减少重复计算布局。
    const scheduleHeightVersionUpdate = useCallback(() => {
      if (heightFrameRef.current) {
        return;
      }

      heightFrameRef.current = requestAnimationFrame(() => {
        heightFrameRef.current = null;
        setHeightVersion((prev) => prev + 1);
      });
    }, []);

    // 滚动时每帧最多更新一次scrollTop，避免频繁触发React渲染。
    const handleQueueScroll = useCallback((event) => {
      pendingScrollTopRef.current = event.currentTarget.scrollTop;
      if (scrollFrameRef.current) {
        return;
      }

      scrollFrameRef.current = requestAnimationFrame(() => {
        scrollFrameRef.current = null;
        setScrollTop(pendingScrollTopRef.current);
      });
    }, []);

    useEffect(() => {
      if (!showQueuebar) {
        if (scrollFrameRef.current) {
          cancelAnimationFrame(scrollFrameRef.current);
          scrollFrameRef.current = null;
        }
        setScrollTop(0);
        return undefined;
      }

      // 记录queuebar尺寸和标题高度，给虚拟列表计算可见范围。
      const updateQueueListMetrics = () => {
        setQueueListHeight(queueListRef.current?.clientHeight ?? 0);
        setVirtualListTop(virtualListRef.current?.offsetTop ?? 0);
        setRowMetrics({
          estimatedHeight: calculateRemToPx(QUEUE_ESTIMATED_ITEM_HEIGHT_REM),
          gap: calculateRemToPx(QUEUE_ITEM_GAP_REM),
        });
      };

      // 首次打开后再补测一次，避免父层布局未稳定导致首屏空白。
      updateQueueListMetrics();
      measureFrameRef.current = requestAnimationFrame(updateQueueListMetrics);
      const resizeObserver = window.ResizeObserver ? new ResizeObserver(updateQueueListMetrics) : null;
      if (resizeObserver && queueListRef.current) {
        resizeObserver.observe(queueListRef.current);
      }
      window.addEventListener('resize', updateQueueListMetrics);

      return () => {
        if (measureFrameRef.current) {
          cancelAnimationFrame(measureFrameRef.current);
        }
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
        window.removeEventListener('resize', updateQueueListMetrics);
      };
    }, [showQueuebar]);

    useEffect(() => {
      // 刷新队列后清掉已不存在病人的高度缓存。
      const activeKeys = {};
      _.forEach(queuePatientList, (item, index) => {
        activeKeys[getQueuePatientKey(item, index)] = true;
      });

      _.forEach(rowHeightsRef.current, (height, key) => {
        if (!activeKeys[key]) {
          delete rowHeightsRef.current[key];
        }
      });

      scheduleHeightVersionUpdate();
    }, [queuePatientList, scheduleHeightVersionUpdate]);

    useEffect(() => {
      return () => {
        if (heightFrameRef.current) {
          cancelAnimationFrame(heightFrameRef.current);
        }
        if (scrollFrameRef.current) {
          cancelAnimationFrame(scrollFrameRef.current);
        }
        _.forEach(rowObserversRef.current, (observer) => observer.disconnect());
      };
    }, []);

    const rowLayout = useMemo(() => {
      // 根据已测量的真实高度计算每张卡的位置和总滚动高度。
      let totalHeight = 0;
      const offsets = [];
      const heights = [];

      _.forEach(queuePatientList, (item, index) => {
        const rowKey = getQueuePatientKey(item, index);
        const rowHeight = rowHeightsRef.current[rowKey] ?? rowMetrics.estimatedHeight;

        offsets[index] = totalHeight;
        heights[index] = rowHeight;
        totalHeight += rowHeight;

        if (index < queuePatientCount - 1) {
          totalHeight += rowMetrics.gap;
        }
      });

      return {
        heights,
        offsets,
        totalHeight,
      };
    }, [heightVersion, queuePatientCount, queuePatientList, rowMetrics.estimatedHeight, rowMetrics.gap]);

    const visibleRange = useMemo(() => {
      // 只渲染可见区域附近的病人卡，保留少量overscan避免滚动露白。
      if (!showQueuebar || queuePatientCount === 0) {
        return { startIndex: 0, endIndex: 0 };
      }

      const listScrollTop = Math.max(scrollTop - virtualListTop, 0);
      const viewportBottom = listScrollTop + queueListHeight;
      const overscan = rowMetrics.estimatedHeight * QUEUE_OVERSCAN_COUNT;
      const startBoundary = Math.max(listScrollTop - overscan, 0);
      const endBoundary = viewportBottom + overscan;
      let startIndex = 0;
      let endIndex = queuePatientCount;
      const minEndIndex = Math.min(QUEUE_MIN_RENDER_COUNT, queuePatientCount);

      while (
        startIndex < queuePatientCount &&
        rowLayout.offsets[startIndex] + rowLayout.heights[startIndex] < startBoundary
      ) {
        startIndex += 1;
      }

      endIndex = startIndex;
      while (endIndex < queuePatientCount && rowLayout.offsets[endIndex] <= endBoundary) {
        endIndex += 1;
      }

      return {
        startIndex,
        endIndex: Math.min(Math.max(endIndex, minEndIndex), queuePatientCount),
      };
    }, [
      queueListHeight,
      queuePatientCount,
      rowLayout.heights,
      rowLayout.offsets,
      rowMetrics.estimatedHeight,
      scrollTop,
      showQueuebar,
      virtualListTop,
    ]);
    const visibleQueuePatients = useMemo(
      () => queuePatientList?.slice(visibleRange.startIndex, visibleRange.endIndex) ?? [],
      [queuePatientList, visibleRange.endIndex, visibleRange.startIndex]
    );

    const handleMeasureRow = (rowKey) => (node) => {
      // 记录每张病人卡真实高度，内容变高时自动重新计算位置。
      if (rowObserversRef.current[rowKey]) {
        rowObserversRef.current[rowKey].disconnect();
        delete rowObserversRef.current[rowKey];
      }

      if (!node) {
        return;
      }

      const updateRowHeight = () => {
        const rowHeight = Math.ceil(node.getBoundingClientRect().height);
        if (rowHeight > 0 && rowHeightsRef.current[rowKey] !== rowHeight) {
          rowHeightsRef.current[rowKey] = rowHeight;
          scheduleHeightVersionUpdate();
        }
      };

      updateRowHeight();

      if (window.ResizeObserver) {
        const observer = new ResizeObserver(updateRowHeight);
        observer.observe(node);
        rowObserversRef.current[rowKey] = observer;
      }
    };

    return (
      <Box className={classes.container} ref={ref}>
        {showQueuebar && (
          <Box className={classes.queueBarContent}>
            <Box className={classes.queueList} ref={queueListRef} onScroll={handleQueueScroll}>
              <Box className={classes.queueTitle}>
                {queueTitle} ({queuePatientCount})
              </Box>
              <Box className={classes.virtualList} ref={virtualListRef} style={{ height: rowLayout.totalHeight }}>
                {_.map(visibleQueuePatients, (item, index) => {
                  const patientIndex = visibleRange.startIndex + index;
                  const rowKey = getQueuePatientKey(item, patientIndex);

                  return (
                    <Box
                      className={classes.virtualRow}
                      key={rowKey}
                      ref={handleMeasureRow(rowKey)}
                      style={{ top: rowLayout.offsets[patientIndex] }}
                    >
                      <EADBed
                        editMode={false}
                        bedData={item}
                        isQueuebar={true}
                        selectedPatient={selectedPatient}
                        handleSelectPatient={handleSelectPatient}
                        handleOpenPatientDetail={handleOpenPatientDetail}
                        showDetails={showDetails}
                        patientInfoViewType={patientInfoViewType}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        )}

        <Box
          className={classes.queueBarToggle}
          onClick={() => {
            setShowQueuebar(!showQueuebar);
            sessionStorage.setItem(queuebarStorageKey, !showQueuebar);
          }}
        >
          <Box className={classes.queueBarArrow}>
            <DropUpIcon className={classes.dropIcon} />
          </Box>
          <Box className={classes.queueBarText}>{queueTitle}</Box>
        </Box>
      </Box>
    );
  }
);

// 选中 floorplan 病床时 QueueBar 数据不变，避免整条队列跟着重渲染。
export default React.memo(QueueBar);
